import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { thumbUrl } from "@/lib/images";
import { Pic } from "@/components/Pic";

/** Vollbild-Bildergalerie (Lightbox): Pfeile, Tastatur (←/→/Esc), Wischen, Zähler, Bildunterschrift, Miniaturen unten. onClose(zuletztGezeigterIndex). actions(i) = optionale Knöpfe in der Kopfzeile (z. B. Admin: Hauptbild/Entfernen). */
export const Lightbox = ({ images, start = 0, alt, caption, onClose, webp = false, actions }) => {
  const [i, setI] = useState(start);
  const n = images.length;
  const touch = useRef(null);
  const stripRef = useRef(null);
  const go = (d) => setI((c) => (c + d + n) % n);
  const close = () => onClose?.(i);

  useEffect(() => { if (n && i >= n) setI(n - 1); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => { if (e.key === "ArrowLeft") go(-1); else if (e.key === "ArrowRight") go(1); else if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = prev; };
  }, [n, i]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { stripRef.current?.querySelector(`[data-idx="${i}"]`)?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }); }, [i]);

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); };

  return (
    <div className="fixed inset-0 z-[1000] bg-[#0b0b0b] text-white flex flex-col select-none" role="dialog" aria-modal="true" data-testid="lightbox" onClick={close}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
        <span className="bg-white/10 rounded px-3 py-1 tabular-nums shrink-0" data-testid="lightbox-counter">{i + 1} / {n}</span>
        {caption?.(i) && <span className="flex-1 text-center font-medium text-base truncate" data-testid="lightbox-caption">{caption(i)}</span>}
        {actions && <div className="flex items-center gap-2 shrink-0" data-testid="lightbox-actions">{actions(i)}</div>}
        <button onClick={close} aria-label="Schließen" className="w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <Pic src={images[i]} webp={webp} alt={alt ? alt(i) : ""} className="max-h-full max-w-full object-contain rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="lightbox-image" />
        {n > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Zurück" data-testid="lightbox-prev"
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors"><ChevronLeft className="w-7 h-7" /></button>
            <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Weiter" data-testid="lightbox-next"
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors"><ChevronRight className="w-7 h-7" /></button>
          </>
        )}
      </div>
      {n > 1 && (
        <div ref={stripRef} className="flex gap-2 overflow-x-auto px-4 py-3 justify-start sm:justify-center" onClick={(e) => e.stopPropagation()} data-testid="lightbox-thumbs">
          {images.map((src, k) => (
            <button key={src + k} data-idx={k} onClick={() => setI(k)} data-testid={`lightbox-thumb-${k}`}
                    className={`shrink-0 w-20 aspect-[4/3] rounded overflow-hidden border-2 transition-all ${k === i ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-90"}`}>
              <Pic src={thumbUrl(src)} webp={webp} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
